import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';

/**
 * Server-side date-range column filter.
 *
 * Renders a filter icon in the column header that opens an overlay with
 * From / To calendars plus Apply / Clear. It does NOT filter client-side —
 * it only emits the selected range so the host screen can re-query the API.
 * Opt-in per column via ITableHeader.serverDateFilter.
 */
@Component({
  selector: 'ngx-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styles: `
    .p-column-filter-menu-button.active-filter,
    .p-column-filter-menu-button.active-filter .pi {
      color: var(--primary-color, #014f7c);
    }
    ::ng-deep .date-range-filter-overlay .p-overlaypanel-content {
      padding: 12px;
    }
  `,
})
export class DateRangeFilterComponent {
  @Input() field = '';
  @Input() column = '';
  /** Seed values so reopening the popup shows the currently applied range. */
  @Input() set from(value: Date | null) {
    this._from = value;
  }
  @Input() set to(value: Date | null) {
    this._to = value;
  }

  @Output() apply = new EventEmitter<{
    from: Date | null;
    to: Date | null;
  }>();

  @ViewChild('overlayPanel') overlayPanel!: OverlayPanel;

  _from: Date | null = null;
  _to: Date | null = null;

  get isActive(): boolean {
    return !!(this._from || this._to);
  }

  showOverlay(event: MouseEvent): void {
    this.overlayPanel.toggle(event);
  }

  onApply(): void {
    // Keep the range sane: if both are set and reversed, swap them.
    if (this._from && this._to && this._from > this._to) {
      const tmp = this._from;
      this._from = this._to;
      this._to = tmp;
    }
    this.apply.emit({ from: this._from, to: this._to });
    this.overlayPanel.hide();
  }

  onClear(): void {
    this._from = null;
    this._to = null;
    this.apply.emit({ from: null, to: null });
    this.overlayPanel.hide();
  }

  // Called by the grid when filters are reset (mirrors EnumFilterComponent).
  reset(): void {
    this._from = null;
    this._to = null;
  }
}
